import React, { useEffect, useMemo, useState } from "react";
import { FaLock } from "react-icons/fa";

const DEFAULT_KARENS = {
  avgWeeksLookback: 12,
  deductionPercentOfAvgWeekHours: 20,
  healthyDaysForNewPeriod: 5,
};

export const SickPanel = ({
  absence,
  value,
  onChange,

  // ✅ NEW: company-wide toggle (pass in from parent)
  // true = karens används, false = karens används inte
  karensEnabled = true,

  // ✅ NEW: parent handler to persist company-wide toggle
  // async allowed; if it throws/returns false => revert toggle
  onToggleKarensEnabled,
}) => {
  const initial = useMemo(() => {
    const stored =
      absence?.karensSettings && typeof absence.karensSettings === "object"
        ? absence.karensSettings
        : null;

    return {
      ...DEFAULT_KARENS,
      ...(stored || {}),
      ...(value || {}),
    };
  }, [absence, value]);

  const [local, setLocal] = useState(initial);

  // Local UI mirror for the toggle (so we can revert on cancel/failure)
  const [localKarensEnabled, setLocalKarensEnabled] = useState(Boolean(karensEnabled));

  useEffect(() => {
    setLocal(initial);
  }, [initial]);

  useEffect(() => {
    setLocalKarensEnabled(Boolean(karensEnabled));
  }, [karensEnabled]);

  const update = (patch) => {
    const next = { ...local, ...patch };
    setLocal(next);
    if (typeof onChange === "function") onChange(next);
  };

  const clampInt = (v, min, max, fallback) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return fallback;
    const i = Math.round(n);
    return Math.min(max, Math.max(min, i));
  };

  // ✅ NEW: toggle handler with confirm warning + revert logic
  const handleToggleKarens = async (nextValue) => {
    const next = Boolean(nextValue);
    const prev = localKarensEnabled;

    if (next === prev) return;

    const warningText = next
      ? [
          "Aktivera karens?",
          "",
          "Detta gör att karensavdrag beräknas och visas i rapporter.",
          "",
          "Vill du fortsätta?",
        ].join("\n")
      : [
          "Stäng av karens?",
          "",
          "Detta gör att karensavdrag INTE beräknas och INTE visas i rapporter.",
          "Sjukfrånvaro-timmar finns kvar, men 'Karens' blir 0 i alla beräkningar.",
          "",
          "Vill du fortsätta?",
        ].join("\n");

    const ok = window.confirm(warningText);
    if (!ok) {
      // Revert UI immediately
      setLocalKarensEnabled(prev);
      return;
    }

    // Optimistic UI
    setLocalKarensEnabled(next);

    // Persist via parent callback if provided
    if (typeof onToggleKarensEnabled === "function") {
      try {
        const res = await onToggleKarensEnabled(next);

        // If parent returns explicit false, treat as failure and revert
        if (res === false) {
          setLocalKarensEnabled(prev);
        }
      } catch (err) {
        console.error("❌ Failed to toggle karensEnabled", err);
        setLocalKarensEnabled(prev);
      }
    }
  };

  return (
    <div className="sickpanel">
      <div className="sickpanel__header">
        <div className="sickpanel__icon">
          <FaLock />
        </div>

        <div className="sickpanel__headtext" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, width: "100%" }}>
          <div>
            <div className="sickpanel__title">Karens – beräkning</div>
          </div>

          {/* ✅ NEW: Company-wide toggle with warning */}
          <label
            className="sickpanel__toggle"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              padding: "6px 10px",
              border: "1px solid #d1d5db",
              borderRadius: 10,
              background: "#fff",
              userSelect: "none",
              whiteSpace: "nowrap",
            }}
            title="Slå på/av karens för hela företaget"
          >
            <span style={{ fontSize: 12, fontWeight: 800, color: "#111827" }}>
              Karens
            </span>

            <input
              type="checkbox"
              checked={localKarensEnabled}
              onChange={(e) => handleToggleKarens(e.target.checked)}
              style={{ margin: 0 }}
            />

            <span style={{ fontSize: 12, fontWeight: 800, color: localKarensEnabled ? "#111827" : "#6b7280" }}>
              {localKarensEnabled ? "Aktiverat" : "Avaktiverat"}
            </span>
          </label>
        </div>
      </div>

      <div className="sickpanel__card sickpanel__card--single">
        <div className="sickpanel__field">
          <label>Genomsnitt baseras på (veckor)</label>
          <input
            type="number"
            min="1"
            max="52"
            step="1"
            value={local.avgWeeksLookback}
            onChange={(e) =>
              update({
                avgWeeksLookback: clampInt(
                  e.target.value,
                  1,
                  52,
                  DEFAULT_KARENS.avgWeeksLookback
                ),
              })
            }
          />
          <div className="sickpanel__help">
            Hur många veckor bakåt som används när genomsnittlig veckoarbetstid räknas ut.
          </div>
        </div>

        <div className="sickpanel__field">
          <label>Karensavdrag (% av genomsnittlig vecka)</label>
          <input
            type="number"
            min="0"
            max="100"
            step="1"
            value={local.deductionPercentOfAvgWeekHours}
            onChange={(e) =>
              update({
                deductionPercentOfAvgWeekHours: clampInt(
                  e.target.value,
                  0,
                  100,
                  DEFAULT_KARENS.deductionPercentOfAvgWeekHours
                ),
              })
            }
          />
          <div className="sickpanel__help">Standard i Sverige är 20%.</div>
        </div>

        <div className="sickpanel__field">
          <label>Ny sjukperiod efter (friska dagar)</label>
          <input
            type="number"
            min="0"
            max="30"
            step="1"
            value={local.healthyDaysForNewPeriod}
            onChange={(e) =>
              update({
                healthyDaysForNewPeriod: clampInt(
                  e.target.value,
                  0,
                  30,
                  DEFAULT_KARENS.healthyDaysForNewPeriod
                ),
              })
            }
          />
          <div className="sickpanel__help">
            Standard: 5 friska kalenderdagar i följd.
          </div>
        </div>

        <div className="sickpanel__tip">
          Rekommendation: Lämna dessa inställningar oförändrade om ert företag följer svensk standard för karensavdrag.
        </div>
      </div>
    </div>
  );
};
